﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { createUseStyles } from "react-jss";
import { useIntl } from "~base/Internationalization/IntlContext";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import type { SelfServiceViewAction } from "../Store";
import type { SelfServiceViewState } from "../Types";
import Grid from "~base/Components/Layout/Grid";
import TitleText from "~base/Components/Primitives/TitleText";
import TitleDate from "~base/Components/Primitives/TitleDate";
import SelfServicePageNavigationControls from "../ChildComponents/PageNavigationControls";
import LabelCheckbox from "~base/Components/Primitives/LabelCheckbox";
import { Icon } from "~base/Components/Primitives/Icon";
import Expander from "~base/Components/Layout/Expander";
import classNames from "classnames";
import { useSettings } from "~base/Settings/SettingsContext";
import type { SelfServiceRegistrationTenant } from "~jrad/Services/API/Account/PostSelfServiceRegistration";
import { SoPThemeConstantsV2 } from "~irad/Styling/SoPTheme2025/Sop2025ThemeConstantsV2";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

export interface SelfServicePageTermsOfServiceProps {
    isLoading: boolean;
    state: SelfServiceViewState;
    dispatch: React.Dispatch<SelfServiceViewAction>;
    postRegistrationRequest: () => void;
    tenants: SelfServiceRegistrationTenant[];
}

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    container: {},
    header: {
        color: theme.constants.textColor,
        fontFamily: theme.constants.headerFont,
        fontSize: "24px",
        fontWeight: "normal",
    },
    desc: {
        ...SoPThemeConstantsV2.Typography.Paragraph,
        color: theme.constants.textColor,
        whiteSpace: "pre-line",
    },
    agreementCheckbox: {
        margin: "30px 0",
    },
    agreementCheckboxLabel: {
        color: theme.constants.textColor,
        ...SoPThemeConstantsV2.Typography.Label,

        "& a": {
            color: "#009fdf",

            "&:hover": {
                color: "#002d72",
            },
        },
    },
    agreementSummaryContainer: {
        margin: "10px 0",
        border: "solid 1px #009fdf",
        opacity: 0,

        transition: "opacity 500ms ease",
    },
    agreementSummaryContainerOpen: {
        opacity: 1,
    },
    agreementSummaryToggle: {
        display: "flex",
        alignItems: "center",

        width: "fit-content",

        cursor: "pointer",
        color: theme.constants.primaryColor,

        "&:hover": {
            color: "#002d72",

            "& $agreementSummaryToggleIconButton": {
                backgroundColor: "#002d72",
            },
        },

        marginTop: "15px",
        marginBottom: "5px",
    },
    agreementSummaryToggleIcon: {
        fontFamily: theme.constants.defaultFont,
        fontSize: "28px",
    },
    agreementSummaryToggleIconButton: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        width: "14px",
        height: "14px",
        padding: "5px",
        borderRadius: "50%",

        color: "#ffffff",
        backgroundColor: theme.constants.primaryColor,
    },
    agreementSummaryToggleText: {
        fontFamily: theme.constants.defaultFont,
        fontSize: "18px",
        textDecoration: "underline",
        marginLeft: "10px",
    },
    agreementSummaryText: {
        fontFamily: theme.constants.defaultFont,
        fontSize: "15px",
        color: theme.constants.textColor,
        whiteSpace: "pre-line",
        padding: "40px 25px 40px 25px",
    },
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const PageTermsOfService: React.FunctionComponent<
    SelfServicePageTermsOfServiceProps
> = ({ isLoading, state, dispatch, postRegistrationRequest, tenants }) => {
    const intl = useIntl();
    const classes = useStyles();
    const settings = useSettings();

    const registrationTenant =
        tenants && tenants.length == 1 ? tenants[0].TenantName : null;

    const tenantName = settings.valueAsString("detectedTenantName");
    const tenantDiplayName =
        intl.tryFormatText("VisiFinance.UI.TenantDisplayName") ??
        registrationTenant ??
        tenantName;

    const [showAgreementSummary, setShowAgreementSummary] =
        React.useState(false);

    return (
        <>
            <h2 className={classes.header}>
                {intl.formatText(
                    "VisiFinance.UI.SelfServiceRegister.Agreement.Title"
                )}
            </h2>
            <p
                className={classes.desc}
                dangerouslySetInnerHTML={{
                    __html: intl.formatText(
                        "VisiFinance.UI.SelfServiceRegister.Agreement.Body",
                        ...[tenantDiplayName]
                    ),
                }}
            />
            <LabelCheckbox
                testIdentifier="AcceptTermsOfService"
                className={classes.agreementCheckbox}
                labelClassName={classes.agreementCheckboxLabel}
                padding={[]}
                size="Medium"
                label={intl.formatHtml(
                    "VisiFinance.UI.SelfServiceRegister.Agreement.CheckboxLabel",
                    state.responseData.AgreementLink
                )}
                checked={state.requestData.hasAcceptedToS}
                onChange={(checked) => {
                    dispatch({
                        type: "setRequestData",
                        key: "hasAcceptedToS",
                        value: checked,
                    });
                }}
            />
            <div
                className={classes.agreementSummaryToggle}
                onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();

                    setShowAgreementSummary(!showAgreementSummary);
                }}
            >
                <div className={classes.agreementSummaryToggleIconButton}>
                    <div className={classes.agreementSummaryToggleIcon}>
                        {showAgreementSummary ? "-" : "+"}
                    </div>
                </div>
                <div className={classes.agreementSummaryToggleText}>
                    {intl.formatText(
                        "VisiFinance.UI.SelfServiceRegister.Agreement.ToggleLink"
                    )}
                </div>
            </div>
            <div
                className={classNames(
                    classes.agreementSummaryContainer,
                    showAgreementSummary &&
                        classes.agreementSummaryContainerOpen
                )}
            >
                <Expander
                    testIdentifier="AgreementSummary"
                    expanded={showAgreementSummary}
                >
                    <div
                        className={classes.agreementSummaryText}
                        dangerouslySetInnerHTML={{
                            __html: state.responseData.AgreementText,
                        }}
                    />
                </Expander>
            </div>
            <SelfServicePageNavigationControls
                state={state}
                dispatch={dispatch}
                postRegistrationRequest={postRegistrationRequest}
                disablePrev={isLoading}
                disableNext={!state.requestData.hasAcceptedToS || isLoading}
            />
        </>
    );
};

export default PageTermsOfService;
